import * as React from "react";

import {
  Box,
  makeStyles,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  Typography,
} from "@material-ui/core";

import { BankAccountForm } from "client/domain";
import { BankConnector } from "client/domain/FormBuilder/BankInformation/BankConnector";
import { BankInfoAccordionHeader } from "client/domain/FormBuilder/BankInformation/BankInfoAccordion";
import { useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";

const useStyles = makeStyles((theme) => {
  const { gray30 } = useNormalizedBrandPaletteValues(theme);

  return {
    detailsContainer: {
      padding: "0rem 1rem",
      "& > div": {
        flex: "0 0 100%",
      },
    },
    accordionDetailsSubtitle: {
      fontSize: "0.9rem",
      lineHeight: "1.4rem",
      color: gray30,
      marginBottom: theme.spacing(2),
    },
  };
});

type FormPanel =
  | "Primary Bank Information"
  | "Secondary Bank Information"
  | "Finance Contact";

const steps: FormPanel[] = [
  "Primary Bank Information",
  "Secondary Bank Information",
];

const info = {
  primaryBankInfo: {
    "aria-controls": "primaryBankInformation-content",
    "data-testid": "primaryBankInfo-header",
    id: "primaryBankInformation-header",
    labelkey: "uiManualBankInfoPrimaryBankLabel",
  },
  secondaryBankInfo: {
    "aria-controls": "secondaryBankInfo-content",
    "data-testid": "secondaryBankInfo-header",
    id: "secondaryBankInfo-header",
    labelkey: "uiManualBankInfoSecondaryBankLabel",
  },
};

const StepHeader: React.FC<{ headerText: string }> = ({ headerText }) => {
  const classes = useStyles();

  return (
    <Box mb={2}>
      <Typography className={classes.accordionDetailsSubtitle} variant="body2">
        {headerText}
      </Typography>
    </Box>
  );
};

const PrimaryStep: React.FC<{
  updateToNextStep: (formPanel: FormPanel) => void;
}> = ({ updateToNextStep }) => {
  const t = useTranslation();
  const headerText = t("uiManualbankInfoAccordianPrimaryBankHeader");

  return (
    <BankConnector
      fieldSetName="PrimaryBankFieldSet"
      fallbackForm={
        <BankAccountForm
          fieldSetName="PrimaryBankFieldSet"
          heading={<StepHeader headerText={headerText} />}
          onSubmitSuccess={() => {
            updateToNextStep("Primary Bank Information");
          }}
        />
      }
      updateToNextStep={() => {
        updateToNextStep("Primary Bank Information");
      }}
    />
  );
};

const SecondaryStep: React.FC<{
  expanded: FormPanel | false;
}> = ({ expanded }) => {
  const t = useTranslation();
  const headerText = t("uiManualBankInfoAccordianSecondaryBankHeader");

  if (expanded !== "Secondary Bank Information") {
    return null;
  }

  return (
    <BankConnector
      fieldSetName="SecondaryBankFieldSet"
      fallbackForm={
        <Box display="flex" flexDirection="column" alignItems="center">
          <BankAccountForm
            fieldSetName="SecondaryBankFieldSet"
            heading={<StepHeader headerText={headerText} />}
            includeSkipButton
          />
        </Box>
      }
    />
  );
};

export const BankInfoAccordion: React.FC = () => {
  const [expanded, setExpanded] = React.useState<FormPanel | false>(
    "Primary Bank Information"
  );
  // maps 1:1 with steps array above
  const [currentStepIdx, setCurrentStepIdx] = React.useState<number>(0);
  const classes = useStyles();
  const t = useTranslation();

  React.useEffect(() => {
    if (steps[currentStepIdx]) {
      setExpanded(steps[currentStepIdx]);
    }
  }, [currentStepIdx]);

  const isExpandable = (panel: FormPanel) => {
    const panelIdx = steps.findIndex((p) => p === panel);
    return currentStepIdx >= panelIdx;
  };

  const handleChange = (panel: FormPanel) => (
    event: React.ChangeEvent<Record<string, unknown>>,
    newExpanded: boolean
  ) => {
    // we make sure the user has completed the previous panels
    if (isExpandable(panel)) {
      setExpanded(newExpanded ? panel : false);
    }
  };

  const updateToNextStep = (step: FormPanel) => {
    if (step === steps[currentStepIdx]) {
      setCurrentStepIdx((idx) => idx + 1);
    } else {
      // if the user opens a previously completed panel and clicks "skip this step"
      const stepIdx = steps.findIndex((s) => s === step);
      setExpanded(steps[stepIdx + 1]);
    }
  };

  return (
    <Box>
      <MuiAccordion
        expanded={expanded === "Primary Bank Information"}
        onChange={handleChange("Primary Bank Information")}
        square
      >
        <BankInfoAccordionHeader
          headerLabel={t(info.primaryBankInfo.labelkey)}
          isExpanded={expanded === "Primary Bank Information"}
          isExpandable={isExpandable("Primary Bank Information")}
          shouldShowCheckmark={currentStepIdx > 0}
          stepInfo={info.primaryBankInfo}
        />
        <MuiAccordionDetails role="region" className={classes.detailsContainer}>
          <PrimaryStep updateToNextStep={updateToNextStep} />
        </MuiAccordionDetails>
      </MuiAccordion>
      <MuiAccordion
        expanded={expanded === "Secondary Bank Information"}
        onChange={handleChange("Secondary Bank Information")}
        square
      >
        <BankInfoAccordionHeader
          headerLabel={t(info.secondaryBankInfo.labelkey)}
          isExpanded={expanded === "Secondary Bank Information"}
          isExpandable={isExpandable("Secondary Bank Information")}
          shouldShowCheckmark={currentStepIdx > 1}
          stepInfo={info.secondaryBankInfo}
        />
        <MuiAccordionDetails role="region" className={classes.detailsContainer}>
          <SecondaryStep expanded={expanded} />
        </MuiAccordionDetails>
      </MuiAccordion>
    </Box>
  );
};
